import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Search, RefreshCw, CheckCircle, XCircle, Clock, Filter, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';

const PaymentManagement = ({ transporterId, onStatisticsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    success: 0,
    failed: 0,
    refunded: 0,
    totalAmount: 0,
  });

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await adminAPI.getTransporterPayments(transporterId, params);
      const paymentsData = response.data.results || response.data || [];
      setPayments(paymentsData);
      
      const stats = {
        total: paymentsData.length,
        pending: paymentsData.filter(p => p.status === 'PENDING').length,
        success: paymentsData.filter(p => p.status === 'SUCCESS').length,
        failed: paymentsData.filter(p => p.status === 'FAILED').length,
        refunded: paymentsData.filter(p => p.status === 'REFUNDED').length,
        totalAmount: paymentsData
          .filter(p => p.status === 'SUCCESS')
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
      };
      setStatistics(stats);
      
      if (onStatisticsUpdate) {
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paiements:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paiements',
        variant: 'destructive',
      });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [transporterId, statusFilter, onStatisticsUpdate]);

  useEffect(() => {
    if (transporterId) {
      loadInitialData();
    }
  }, [transporterId, loadInitialData]);

  const handleRefresh = async () => {
    await loadInitialData();
    toast({
      title: 'Succès',
      description: 'Données mises à jour',
    });
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.id?.toLowerCase().includes(searchLower) ||
      payment.method_code?.toLowerCase().includes(searchLower) ||
      payment.status?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="text-sm font-mono text-muted-foreground">
            {payment.id?.substring(0, 8)}...
          </div>
        );
      },
    },
    {
      id: 'booking',
      header: 'Réservation',
      accessorKey: 'booking',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="text-sm">
            {payment.booking?.id ? (
              <span className="font-mono text-muted-foreground">{payment.booking.id.substring(0, 8)}...</span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'payer_user',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="text-sm">
            <p className="font-medium">{payment.payer_user?.client_profile?.company_name || payment.payer_user?.first_name || 'N/A'}</p>
            <p className="text-xs text-muted-foreground">{payment.payer_user?.email || ''}</p>
          </div>
        );
      },
    },
    {
      id: 'amount',
      header: 'Montant',
      accessorKey: 'amount',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="flex items-center gap-1 text-sm font-medium">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span>{payment.amount ? Number(payment.amount).toLocaleString('fr-FR') : '0'} {payment.currency || 'GNF'}</span>
          </div>
        );
      },
    },
    {
      id: 'method',
      header: 'Méthode',
      accessorKey: 'method_code',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="flex items-center gap-1 text-sm">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="capitalize">{payment.method_code || 'N/A'}</span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        const payment = row.original;
        const status = payment.status || 'PENDING';
        const statusConfig = {
          PENDING: { label: 'En attente', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
          SUCCESS: { label: 'Réussi', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
          FAILED: { label: 'Échoué', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
          REFUNDED: { label: 'Remboursé', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: XCircle },
        };
        const config = statusConfig[status] || statusConfig.PENDING;
        const Icon = config.icon;
        return (
          <Badge className={config.className}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Créé le',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <span className="text-sm text-muted-foreground">
            {payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-teal-500" />
            Paiements reçus
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-foreground">{statistics.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-500">{statistics.pending}</p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-500">{statistics.success}</p>
            <p className="text-xs text-muted-foreground">Réussis</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-red-500">{statistics.failed}</p>
            <p className="text-xs text-muted-foreground">Échoués</p>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-orange-500">{statistics.refunded}</p>
            <p className="text-xs text-muted-foreground">Remboursés</p>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-3">
            <p className="text-sm font-bold text-purple-500">
              {statistics.totalAmount.toLocaleString('fr-FR')} GNF
            </p>
            <p className="text-xs text-muted-foreground">Montant total</p>
          </div>
        </div>

        <Separator />

        {/* Filtres */}
        {showFilters && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher par ID, méthode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="PENDING">En attente</option>
                <option value="SUCCESS">Réussi</option>
                <option value="FAILED">Échoué</option>
                <option value="REFUNDED">Remboursé</option>
              </select>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="min-h-[400px]">
          <DataTable
            data={filteredPayments}
            columns={columns}
            loading={loading}
            searchable={false}
            pageSize={10}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentManagement;

